import {
  AfterViewInit,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {TranslateService} from '@ngx-translate/core';
import {Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import {SelectItem} from 'primeng/api';
import {MultiSelect} from 'primeng/multiselect';

import {keyboardKeys} from '../../constants/keyboard-keys.constants';
import {IdName} from '../../models/id-name.model';
import {ValidationElementComponent} from '../validation-element/validation-element.component';
import {GenericFunctionsUtil} from '../../utils/generic-functions.util';
import {ValidationService} from '../../services/validation.service';
import {ResponsiveService} from '../../services/responsive.service';
import {FieldService} from '../../services/field.service';

const DEFAULT_SIZE = '50';

@Component({
  selector: 'si-selector-multiple',
  templateUrl: './selector-multiple.component.html',
  styleUrls: ['./selector-multiple.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SelectorMultipleComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() id: string;
  @Input() name: string;
  @Input() text: string;
  @Input() styleClass: string;
  @Input() disabled: boolean;
  @Input() rule: string;
  @Input() validationExternalErrorText: string;
  @Input() labelAttr = 'name';

  // Name of the attribute that we put in the model.
  // If we initialize the model variable with a value other than an object (such as a 'code' attribute), we must put 'code' in this input.
  @Input() valueAttrInput: string;
  @Input() valueAttr: string;
  @Input() tooltipText: string;
  @Input() tooltipPosition: string;
  @Input() tooltipIcon: string;
  @Input() tooltipStyleClass: string;
  @Input() tooltipEscape = true;
  @Input() forceShowTooltipIcon: boolean;
  @Input() detailMode = false;

  // Name of the attribute of an option to filter by.
  @Input() filterName: string;

  // When filterModel is an array of objects, it is needed the name of the attribute inside the objects to filter by.
  @Input() filterModelArrayName = 'id';

  @Input() backSearchParam = 'q';
  @Input() getOptionsOnOpen = false;
  @Input() hideLabel = false;
  @Input() showClear = true;
  @Input() showToggleAll = true;
  @Output() modelChange = new EventEmitter<any>();
  @Output() selectDefaultChange = new EventEmitter<any>();
  @Output() tooltipIconClick = new EventEmitter<any>();
  @ViewChild(MultiSelect, {static: true}) pMultiSelect: MultiSelect;
  @ViewChild('siSelectorMultipleLoader', {static: true}) siSelectorMultipleLoader: ElementRef;
  @ViewChild(ValidationElementComponent) validationElement: ValidationElementComponent;
  @ContentChild('selectedItems', {static: true}) selectedItems: TemplateRef<ElementRef>;
  @ContentChild('item', {static: true}) item: TemplateRef<ElementRef>;
  isDesktop: boolean;
  showRuleErrorOutsideIcon: boolean;
  validationErrorText: string;
  lastEmittedModel: any[];

  // Options to be shown in the selector.
  options: SelectItem[];

  // Copy of options without any filter.
  optionsOriginal: SelectItem[];

  // Flag to know if the options are loaded.
  optionsLoaded = false;
  placeholderTranslated: string;
  inputFilterSubject = new Subject<string>();
  autofocusFilter: boolean;
  shiftTabFromInput = false;
  displaySelectedLabel = true;
  resetFilterOnHide = true;
  totalRecords: number;
  showResponsiveTemplate: boolean;
  dropdownStyleClass: string;
  scrollHeight = '200px';
  oldOptionsSelected: SelectItem[];
  private clearIconElement: HTMLElement;
  private selectAllLabelElement: HTMLElement;
  private _listeners = [];
  private _panelListeners = [];
  private preselectedOptionIndex = -1;
  private highlightClass = 'si-selector-multiple_item--highlight';
  private _model: any[];
  private _placeholder: string;
  private _values: object[];

  // Value of the attribute of an option to filter by.
  private _filterModel: string | number | any[];

  private _backSearch = false;
  private _path: string;
  private _selectSingle = false;
  private _selectDefault = false;
  private _compact = false;
  private _showDropdownSearch = true;
  private responsiveDropdownLabelElement: HTMLElement;
  private bodyElement: HTMLElement;

  constructor(
    private translateService: TranslateService,
    private http: HttpClient,
    private renderer: Renderer2,
    private validationService: ValidationService,
    private responsiveService: ResponsiveService,
    private fieldService: FieldService
  ) {
    this.setLogic();
  }

  get model() {
    return this._model;
  }

  @Input()
  set model(val: any[]) {
    if (!val || val.length === 0 || val[0] instanceof Object) {
      this._model = val;
    }

    if (!this.backSearch) {
      setTimeout(() => {
        this.loadDefaultModel(val);
      });
    }

    if (!val || val.length === 0) {
      this.displayClearIcon(false);
    } else {
      this.displayClearIcon(true);
    }
  }

  get placeholder() {
    return this._placeholder;
  }

  @Input()
  set placeholder(val) {
    this._placeholder = val;
    this.translateService.get(val).toPromise().then(translation => {
      this.placeholderTranslated = translation;
    });
  }

  get values() {
    return this._values;
  }

  @Input()
  set values(val) {
    this.optionsLoaded = true;
    this.setValuesAsync(val);
  }

  get filterModel() {
    return this._filterModel;
  }

  @Input()
  set filterModel(val) {
    if (val !== this._filterModel) {
      this._filterModel = val;
      this.options = null;
      this.setModelValid();
    }
  }

  get path() {
    return this._path;
  }

  @Input()
  set path(val) {
    if (this.path === val) {
      return;
    }

    this._path = val;
    this.options = null;
    this.optionsOriginal = null;
    // If another field changes, the path changes. So, we need to check if the options selected are valid and update them if it is necessary.
    this.setModelValid();
  }

  get backSearch() {
    return this._backSearch;
  }

  @Input()
  set backSearch(val) {
    // Backsearch is true when val is true or when val is ''.
    if (val === false) {
      this._backSearch = false;
    } else {
      this._backSearch = true;
    }
  }

  get compact() {
    return this._compact;
  }

  @Input()
  set compact(val) {
    this._compact = val;
    if (val) {
      this.scrollHeight = '300px';
      this.displaySelectedLabel = false;
      this.showDropdownSearch = false;
      this.resetFilterOnHide = false;
      this.hideLabel = true;
    }
  }

  get showDropdownSearch() {
    return this._showDropdownSearch;
  }

  @Input()
  set showDropdownSearch(val) {
    this._showDropdownSearch = val;
    if (!val) {
      this.resetFilterOnHide = false;
    }
  }

  get selectSingle(): boolean {
    return this._selectSingle;
  }

  @Input()
  set selectSingle(val) {
    this._selectSingle = val;
    if (val) {
      this.selectIfSingleOption();
    }

  }

  get selectDefault() {
    return this._selectDefault;
  }

  @Input()
  set selectDefault(val) {
    this._selectDefault = true;
    this.getOptions();
  }

  async setValuesAsync(val): Promise<void> {
    const valuesTranslated = await this.translateValues(val);
    this._values = valuesTranslated;
    this.optionsOriginal = this.mapOptionsFromBack(valuesTranslated);
    this.options = GenericFunctionsUtil.deepClone(this.optionsOriginal);

    if (this.selectSingle) {
      this.selectIfSingleOption();
    }
  }

  ngOnInit(): void {
    this.decorateMultiSelect();

    this.inputFilterSubject.asObservable()
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe(() => {
        this.getOptions();
      });
  }

  ngAfterViewInit(): void {
    if (!this.compact && !this.detailMode) {
      this.setClearIcon();
    }

    // If the model comes with value, it is set before ngAfterViewInit, so we have to check it to display the clear icon after creating it.
    if (this.model && this.model.length) {
      this.displayClearIcon();
    }
  }

  setLogic(): void {
    this.isDesktop = this.responsiveService.isDesktop();
    this.showRuleErrorOutsideIcon = this.validationService.getShowRuleErrorOutsideIcon();
    this.showResponsiveTemplate = !this.isDesktop && this.fieldService.getSelectorMultipleResponsiveTemplate();
    this.dropdownStyleClass = this.showResponsiveTemplate ? 'responsive-dropdown-panel' : '';
    this.scrollHeight = this.showResponsiveTemplate ? '400px' : this.scrollHeight;
    this.autofocusFilter = this.isDesktop;
  }

  setClearIcon(): void {
    this.clearIconElement = this.renderer.createElement('i');
    this.renderer.setAttribute(this.clearIconElement, 'id', 'siSelectorMultipleClearIcon');
    this.renderer.addClass(this.clearIconElement, 'icon-closex');
    this.renderer.addClass(this.clearIconElement, 'si-selector-multiple_clean_icon');
    this.renderer.addClass(this.clearIconElement, 'display-none');

    const pMultiSelectElement = this.pMultiSelect.el.nativeElement;
    const uiMultiSelectElement = pMultiSelectElement.querySelector('.p-multiselect');
    const uiTriggerIconElement = pMultiSelectElement.querySelector('.p-multiselect-trigger');
    this.renderer.insertBefore(uiMultiSelectElement, this.clearIconElement, uiTriggerIconElement);
    this._listeners.push(
      this.renderer.listen(this.clearIconElement, 'click', (event) => this.onClickClearIcon(event))
    );
  }

  displayClearIcon(displayFlag = true): void {
    if (!this.showClear || !this.clearIconElement) {
      return;
    }

    if (displayFlag) {
      this.renderer.removeClass(this.clearIconElement, 'display-none');
    } else {
      this.renderer.addClass(this.clearIconElement, 'display-none');
    }
  }

  removeTabIndexOfOptions(): void {
    const pMultiSelectElement = this.pMultiSelect.el.nativeElement;
    const uiMultiSelectOptionElements = pMultiSelectElement.querySelectorAll('.p-multiselect-item');
    uiMultiSelectOptionElements.forEach(el => this.renderer.removeAttribute(el, 'tabindex'));
  }

  onChangeMultiSelectModel({originalEvent, value}) {
    if (this.showResponsiveTemplate) {
      originalEvent?.stopPropagation();
      return;
    }

    this.emitModel(value);
  }

  async onClickMultiSelect(): Promise<void> {
    if (this.pMultiSelect.overlayVisible) {
      return;
    }

    this.preselectedOptionIndex = -1;
    await this.getOptions();
    setTimeout(() => {
      this.removeTabIndexOfOptions();
      this.setSelectAllElement();
      if (this.showResponsiveTemplate) {
        this.setResponsiveMaskDropdown();
        this.setResponsiveLabel();
        this.setResponsiveDropdownSearchClass();
        this.bodyElement = document.querySelector('body');
        this.bodyElement.style.overflowY = 'hidden';

        if (this.options?.length) {
          this.setResponsiveDropdownButtons();
        }
      }
    });
  }

  onHideMultiSelect(): void {
    if (this.backSearch) {
      this.options = [];
    }

    if (this.bodyElement) {
      this.bodyElement.style.overflowY = 'scroll';
    }
  }

  onClickClearIcon(event): void {
    event.stopPropagation();
    this.removeOptions();
  }

  // Create 'Select all' label next to the checkbox.
  async setSelectAllElement(): Promise<void> {
    if (!this.showToggleAll) {
      return;
    }

    const pMultiSelectElement = this.pMultiSelect.el.nativeElement;
    const pCheckboxElement = pMultiSelectElement.querySelector('.p-checkbox');
    const selectAllText = await this.translateService.get('CORE_SELECT_ALL').toPromise();
    this.selectAllLabelElement = this.renderer.createElement('span');
    this.renderer.addClass(this.selectAllLabelElement, 'si-selector-multiple_select-all');
    this.renderer.setProperty(this.selectAllLabelElement, 'innerHTML', selectAllText);
    this.renderer.appendChild(pCheckboxElement, this.selectAllLabelElement);
    this._panelListeners.push(
      this.renderer.listen(pCheckboxElement, 'click', () => this.pMultiSelect.toggleAll(event))
    );
  }

  // Create total count element.
  async setTotalCountElement(records: number, totalRecords: number): Promise<void> {
    // Set dropdown element
    const multiselectElement = this.pMultiSelect.el.nativeElement.querySelector('.ui-multiselect-panel');
    let totalCountElement = this.pMultiSelect.el.nativeElement.querySelector('.si-selector-multiple_total-count');

    // Translate texts
    const totalCountText = await this.translateService
      .get('CORE_SHOWING_RESULTS_OF', {records, totalRecords}).toPromise();

    if (multiselectElement) {
      if (!totalCountElement) {
        // Create totalCount element
        totalCountElement = this.renderer.createElement('div');
        totalCountElement.classList.add('si-selector-multiple_total-count');
        this.renderer.appendChild(multiselectElement, totalCountElement);
      }
      this.renderer.setProperty(totalCountElement, 'innerHTML', totalCountText);
    }
  }

  setResponsiveDropdownSearchClass(): void {
    const dropdownPanelElement = this.pMultiSelect.el.nativeElement.querySelector('.p-multiselect-panel');
    if (dropdownPanelElement && this.showDropdownSearch) {
      this.renderer.addClass(dropdownPanelElement, 'si-selector-multiple_p-dropdown-panel-with-search');
    }
  }

  async setResponsiveDropdownButtons(): Promise<void> {
    const dropdownPanelElement = this.pMultiSelect.el.nativeElement.querySelector('.p-multiselect-panel');
    const acceptText = await this.translateService.get('CORE_ACCEPT').toPromise();
    const cancelText = this.translateService.instant('CORE_CANCEL');

    if (dropdownPanelElement) {
      this.renderer.addClass(dropdownPanelElement, 'si-selector-multiple_p-dropdown-panel-with-buttons');
      const buttonsDiv = this.renderer.createElement('div');
      this.renderer.addClass(buttonsDiv, 'si-selector-multiple_dropdown-panel-buttons');

      const buttonAcceptDiv = this.renderer.createElement('div');
      this.renderer.addClass(buttonAcceptDiv, 'si-selector-multiple_dropdown-panel-accept-button');
      const buttonAccept = this.renderer.createElement('button');
      this.renderer.setAttribute(buttonAccept, 'id', 'siSelectorMultipleResponsiveAcceptButton');
      this.renderer.setAttribute(buttonAccept, 'type', 'button');
      this.renderer.addClass(buttonAccept, 'si-button_main');
      this.renderer.addClass(buttonAccept, 'si-selector-multiple_overlay-closable');
      buttonAccept.innerText = acceptText;
      this._listeners.push(
        this.renderer.listen(buttonAccept, 'click', (event) => this.onClickAcceptButton(event))
      );
      buttonAcceptDiv.appendChild(buttonAccept);

      const buttonCancelDiv = this.renderer.createElement('div');
      this.renderer.addClass(buttonCancelDiv, 'si-selector-multiple_dropdown-panel-cancel-button');
      const buttonCancel = this.renderer.createElement('button');
      this.renderer.setAttribute(buttonCancel, 'id', 'siSelectorMultipleResponsiveCancelButton');
      this.renderer.setAttribute(buttonCancel, 'type', 'button');
      this.renderer.addClass(buttonCancel, 'si-button_secondary');
      this.renderer.addClass(buttonCancel, 'si-selector-multiple_overlay-closable');
      buttonCancel.innerText = cancelText;
      this._listeners.push(
        this.renderer.listen(buttonCancel, 'click', () => this.onClickCancelButton())
      );
      buttonCancelDiv.appendChild(buttonCancel);

      buttonsDiv.appendChild(buttonCancelDiv);
      buttonsDiv.appendChild(buttonAcceptDiv);
      dropdownPanelElement.appendChild(buttonsDiv);
    }
  }

  setResponsiveMaskDropdown(): void {
    const dropdownPanelElement = this.pMultiSelect.el.nativeElement.querySelector('.p-multiselect-panel');
    if (dropdownPanelElement) {
      const maskDiv = this.renderer.createElement('div');
      this.renderer.addClass(maskDiv, 'si-selector-multiple_dropdown-panel-mask');
      this.renderer.addClass(maskDiv, 'si-selector-multiple_overlay-closable');
      this._listeners.push(
        this.renderer.listen(maskDiv, 'click', (event) => this.onClickResponsiveMask())
      );
      dropdownPanelElement.appendChild(maskDiv);
    }
  }

  async setResponsiveLabel(): Promise<void> {
    const text = this.text || this.placeholder || 'CORE_SELECT';
    const labelText = await this.translateService.get(text).toPromise();
    const dropdownPanelElement = this.pMultiSelect.el.nativeElement.querySelector('.p-multiselect-panel');
    if (dropdownPanelElement) {
      if (!this.responsiveDropdownLabelElement) {
        this.renderer.addClass(dropdownPanelElement, 'si-selector-multiple_p-dropdown-panel-with-label');
        this.responsiveDropdownLabelElement = this.renderer.createElement('div');
        this.renderer.addClass(this.responsiveDropdownLabelElement, 'si-label');
        this.renderer.addClass(this.responsiveDropdownLabelElement, 'si-selector-multiple_dropdown-panel-label');
        const label = this.renderer.createElement('label');
        label.innerText = `${labelText}`;
        this.responsiveDropdownLabelElement.appendChild(label);
      }

      this.renderer.insertBefore(dropdownPanelElement, this.responsiveDropdownLabelElement, dropdownPanelElement.firstChild);
    }
  }

  onClickAcceptButton(event: Event): void {
    this.oldOptionsSelected = null;
    this.pMultiSelect.onModelChange(this.pMultiSelect.value);
    this.pMultiSelect.onChange.emit({
      value: this.pMultiSelect.value
    });
    this.pMultiSelect.updateLabel();
    this.pMultiSelect.updateFilledState();
    this.emitModel(this.pMultiSelect.value);
  }

  onClickCancelButton(): void {
    this.pMultiSelect.value = this.oldOptionsSelected;
    this.oldOptionsSelected = null;
  }

  onClickResponsiveMask() {
    this.onClickCancelButton();
  }

  async getOptions(): Promise<void> {
    if (this.optionsOriginal && !this.backSearch && !this.getOptionsOnOpen) {
      this.filterOptions();
      return;
    }

    let httpParams = new HttpParams();
    if (this.backSearch) {
      httpParams = httpParams
        .set('size', DEFAULT_SIZE)
        .set(this.backSearchParam, this.pMultiSelect.filterValue || '');
    }

    if (this.siSelectorMultipleLoader) {
      this.renderer.addClass(this.siSelectorMultipleLoader.nativeElement, 'loading');
    }

    this.optionsLoaded = false;
    const response = await this.http.get(this.path, {params: httpParams, observe: 'response'}).toPromise();
    this.optionsOriginal = this.mapOptionsFromBack(response.body as IdName[]);
    this.options = GenericFunctionsUtil.deepClone(this.optionsOriginal);
    if (this.backSearch && response.headers.get('x-total-count')) {
      this.totalRecords = parseInt(response.headers.get('x-total-count'), 10);
      await this.setTotalCountElement(this.options.length, this.totalRecords);
    }

    if (this.siSelectorMultipleLoader) {
      this.renderer.removeClass(this.siSelectorMultipleLoader.nativeElement, 'loading');
    }

    // Align the overlay element again after loading the options.
    this.pMultiSelect.alignOverlay();
    this.optionsLoaded = true;
  }

  mapOptionsFromBack(options): SelectItem[] {
    if (!options || !options.length) {
      return [];
    }

    const defaultOptions = [];
    const optionsParsed = options.map(option => {
      const optionParsed = {
        label: option[this.labelAttr],
        value: option
      };

      if (!this.model?.length && this.selectDefault && option.isDefault) {
        defaultOptions.push(option);
      }

      return optionParsed;
    });

    if (defaultOptions.length) {
      const defaultOptionsParsed = this.valueAttr ? defaultOptions.map(val => val[this.valueAttr]) : defaultOptions;
      this.selectDefaultChange.emit(defaultOptionsParsed);
    }

    return optionsParsed;
  }

  async translateValues(values): Promise<object[]> {
    if (values) {
      for (let i = 0; i < values.length; i++) {
        values[i][this.labelAttr] = await this.translateService.get(values[i][this.labelAttr]).toPromise();
      }
    }

    return values;
  }

  /**
   * Filter the options by an attribute.
   * @Input filterName Name of the attribute we want to filter.
   * @Input filterModel Value to be filtered.
   * @Input filterModelArrayName If filterModel is an array ob objects, this is the name of the attribute inside the objects we want to filter.
   */
  filterOptions(): void {
    if (
      !this.optionsOriginal?.length
      || !this.filterName
      || !this.filterModel
      || (Array.isArray(this.filterModel) && !this.filterModel.length)
    ) {
      // No needed to filter or nothing to filter.
      this.options = GenericFunctionsUtil.deepClone(this.optionsOriginal);
    } else {
      this.options = this.optionsOriginal.filter(option => {
        // filterModel has multiple values.
        if (Array.isArray(this.filterModel)) {
          const optionAttr: string | number = option.value[this.filterName];
          return this.filterModel.find(e => e === optionAttr || e[this.filterModelArrayName] === optionAttr);
        }

        // filterModel has a single value.
        return option.value[this.filterName] === this.filterModel;
      });
    }
  }

  emitModel(model: IdName[]): void {
    if (!model?.length) {
      this.modelChange.emit(model);
      this.setLastEmittedModel(model);
      return;
    }

    const modelToEmit = this.valueAttr ? model.map((val) => val[this.valueAttr]) : model;
    if (
      (this.valueAttr && !GenericFunctionsUtil.areSameArraysSimple(this.lastEmittedModel, modelToEmit))
      || (!this.valueAttr && !GenericFunctionsUtil.areSameArraysObject(this.lastEmittedModel, model, this.valueAttrInput || 'id'))
    ) {
      this.modelChange.emit(modelToEmit);
      this.setLastEmittedModel(model);
    }
  }

  setLastEmittedModel(model: any): void {
    this.lastEmittedModel = (this.valueAttr && model) ? model.map((val) => val[this.valueAttr]) : model;
  }

  // Load a default model passed by parameter.
  async loadDefaultModel(val: IdName[]): Promise<void> {
    if (val) {
      // If val is an object, set it as normal.
      // Else, it has to be the string in valueAttrInput, valueAttr or 'id' (default). So, we have to retrieve the options, find the object
      // by the valueAttrInput and set it in selectedOption.
      if (!val.length || val[0] instanceof Object) {
        this.pMultiSelect.value = val;
        this.pMultiSelect.updateLabel();
      } else {
        await this.getOptions();
        this.pMultiSelect.value = this.options
          .filter(option => val.includes(option.value[this.valueAttrInput]) || val.includes(option.value[this.valueAttr || 'id']))
          .map(option => option.value);
        this._model = this.pMultiSelect.value;
        // Check again to display clear icon due to:
        // 1) When the component is initialized the clear icon is not created yet.
        // 2) When ngAfterViewInit is launched, the model is not filled yet because we are waiting to get the options.
        if (!this._model || this._model.length === 0) {
          this.displayClearIcon(false);
        } else {
          this.displayClearIcon(true);
        }
      }

      /**
       * Emit the model when:
       * The model input is an object array but the selector has valueAttr -> The expected output is an array of the value of the valueAttr.
       * The model input is another type of array (it is not an array of objects) and the selector does not have valueAttr -> The expected
       * output is the complete object array.
       * The model input is another type of array (it is not an array of objects) and the selector has valueAttrInput and valueAttr -> The
       * expected output is an array of the value of the valueAttr and not the valueAttrInput one.
       */
      if (
        val.length
        && (
          (val[0] instanceof Object && this.valueAttr)
          || (!(val[0] instanceof Object) && !this.valueAttr)
          || (!(val[0] instanceof Object) && this.valueAttrInput && this.valueAttr && val[0] !== this._model[0][this.valueAttr])
        )
      ) {
        this.emitModel(this.pMultiSelect.value);
      }
    }

    this.setLastEmittedModel(val);
  }

  // Get the options and select the single one if there is only one option. It works like the old af-select-single.
  async selectIfSingleOption(): Promise<void> {
    if (this.path) {
      await this.getOptions();
    }

    if (this.options.length !== 1) {
      return;
    }

    this.model = [this.options[0].value];
    const singleOptionParsed = this.valueAttr ? this.options[0].value[this.valueAttr] : this.options[0].value;
    this.selectDefaultChange.emit([singleOptionParsed]);
  }

  // Check if each option of the model is in the options and set only the valid ones.
  async setModelValid(): Promise<void> {
    if ((!this.model || !this.model.length) && !this.validationElement?.showRequiredElementFlag) {
      return;
    }

    if (!this.options || !this.options.length) {
      await this.getOptions();
    }

    if (this.model && this.model.length) {
      const attrToRead = this.valueAttrInput || this.valueAttr || 'id';
      this.model = this.model.filter(modelOption => {
        return this.options.some(option => option.value[attrToRead] === modelOption[attrToRead]);
      });
    }

    this.checkOnlyOneOption();
    this.emitModel(this.model);
  }

  private checkOnlyOneOption(): void {
    if (!this.model) {
      this.model = [];
    }

    // Set the model if the selector is required and there is only one option to select.
    if (!this.validationElement?.showRequiredElementFlag || this.options?.length !== 1 || this.model.length !== 0) {
      return;
    }

    this.model = [this.options[0].value];
  }

  openMultiSelect(e: any): void {
    setTimeout(() => {
      const input = this.pMultiSelect.el.nativeElement.querySelector('input');
      if (!input) {
        return;
      }

      this.pMultiSelect.onMouseclick(e, input);
    });
  }

  removeOption(event, item): void {
    event.stopPropagation();

    const selectionIndex = this.pMultiSelect.findSelectionIndex(item);
    this.pMultiSelect.value = this.pMultiSelect.value.filter(function (val, i) {
      return i != selectionIndex;
    });
    if (this.pMultiSelect.selectionLimit) {
      this.pMultiSelect.maxSelectionLimitReached = false;
    }

    this.pMultiSelect.onModelChange(this.pMultiSelect.value);
    this.pMultiSelect.onChange.emit({
      originalEvent: event.originalEvent,
      value: this.pMultiSelect.value,
      itemValue: item
    });
    this.pMultiSelect.updateLabel();
    this.pMultiSelect.updateFilledState();
  }

  removeOptions(): void {
    this.displayClearIcon(false);
    this.pMultiSelect.value = [];
    if (this.pMultiSelect.overlayVisible) {
      this.pMultiSelect.hide();
    }

    this.emitModel([]);
  }

  // Functions overwriting the real ones in the MultiSelect component.
  async decorateMultiSelect(): Promise<void> {
    if (this.pMultiSelect) {
      // ActivateFilter overwritten because the pMultiSelect only filter in front.
      // So, we implement the inputFilterSubject to filter in back.
      const originalActivateFilter = this.pMultiSelect.activateFilter.bind(this.pMultiSelect);
      this.pMultiSelect.activateFilter = () => {
        if (!this.backSearch) {
          originalActivateFilter();
          return;
        }

        this.inputFilterSubject.next(this.pMultiSelect.filterValue);
      };

      // OnFilterInputChange overwritten because when the filter is in back we need to filter even when the inputValue is null.
      const originalOnFilterInputChange = this.pMultiSelect.onFilterInputChange.bind(this.pMultiSelect);
      this.pMultiSelect.onFilterInputChange = (event: KeyboardEvent) => {
        this.resetHighlightOptions();
        originalOnFilterInputChange(event);
      };

      // UpdateLabel overwritten because we want the label even if an option is not in the options.
      this.pMultiSelect.updateLabel = () => {
        if (this.pMultiSelect.value && this.pMultiSelect.value.length && this.pMultiSelect.displaySelectedLabel) {
          let label = '';
          // Add option labels only until maxSelectedLabels is reached.
          for (let i = 0; i < this.pMultiSelect.value.length && i < this.pMultiSelect.maxSelectedLabels; i++) {
            // If the option is not in options (or options does not exist), get its name: this.value[i].name
            let optionLabel = this.pMultiSelect.options ? this.pMultiSelect.findLabelByValue(this.pMultiSelect.value[i]) : null;
            optionLabel = optionLabel ? optionLabel : this.pMultiSelect.value[i][this.labelAttr];
            label = label.length > 0 ? label + ', ' : label;
            label = label + optionLabel;
          }

          this.pMultiSelect.valuesAsString = label;
        } else {
          this.pMultiSelect.valuesAsString = this.pMultiSelect.placeholder;
        }
      };

      // OnOptionClick overwritten because we want to keep the focus on the filter input and allow the user to select options with keys,
      // and to avoid changing and emitting the model if we are in responsive mode (with buttons).
      const originalOnOptionClick = this.pMultiSelect.onOptionClick.bind(this.pMultiSelect);
      this.pMultiSelect.onOptionClick = (event) => {
        if (!event.option){
          return;
        }

        const optionElements = this.pMultiSelect.el.nativeElement.querySelector('.p-multiselect-items').children;
        // Remove highlight class of previous preselected option.
        if (this.preselectedOptionIndex >= 0) {
          this.renderer.removeClass(optionElements[this.preselectedOptionIndex].firstChild, this.highlightClass);
        }

        // Assign the new preselected option index.
        this.preselectedOptionIndex = this.pMultiSelect.optionsToRender.findIndex(option => option.value.id === event.option.value.id);
        this.renderer.addClass(optionElements[this.preselectedOptionIndex].firstChild, this.highlightClass);
        // Focus on input filter.
        if (this.autofocusFilter && this.pMultiSelect.filterInputChild) {
          this.pMultiSelect.filterInputChild.nativeElement.focus();
        }

        if (!this.showResponsiveTemplate) {
          originalOnOptionClick(event);
        } else {
          let option = event.option;
          if (this.pMultiSelect.isOptionDisabled(option)) {
            return;
          }

          const optionValue = this.pMultiSelect.getOptionValue(option);
          const selectionIndex = this.pMultiSelect.findSelectionIndex(optionValue);
          if (selectionIndex != -1) {
            this.pMultiSelect.value = this.pMultiSelect.value.filter((val, i) => i != selectionIndex);
            if (this.pMultiSelect.selectionLimit) {
              this.pMultiSelect.maxSelectionLimitReached = false;
            }
          } else {
            if (
              !this.pMultiSelect.selectionLimit
              || (!this.pMultiSelect.value || this.pMultiSelect.value.length < this.pMultiSelect.selectionLimit)
            ) {
              this.pMultiSelect.value = [...this.pMultiSelect.value || [], optionValue];
            }

            this.pMultiSelect.checkSelectionLimit();
          }
        }
      };

      // Show overwritten because we want to allow the user to select options with keys.
      const originalShow = this.pMultiSelect.show.bind(this.pMultiSelect);
      this.pMultiSelect.show = () => {
        this.removePanelListeners();
        originalShow(event);

        if (this.showResponsiveTemplate && !this.oldOptionsSelected) {
          this.oldOptionsSelected = this.pMultiSelect.value ? [...this.pMultiSelect.value] : [];
        }

        setTimeout(() => {
          if (this.pMultiSelect.filterInputChild) {
            this._panelListeners.push(
              this.renderer.listen(this.pMultiSelect.filterInputChild.nativeElement, 'keydown', () => this.onKeyDownInput(event))
            );
          }
        });
      };

      // AlignOverlay overwritten to add 2 pixels to the overlay element because if not, the blue animation cannot be seen.
      const originalAlignOverlay = this.pMultiSelect.alignOverlay.bind(this.pMultiSelect);
      this.pMultiSelect.alignOverlay = function () {
        setTimeout(() => {
          originalAlignOverlay();
          if (this.overlay) {
            const top = this.overlay.style.top;
            this.overlay.style.top = (parseInt(top.substring(0, top.length - 2), 10) - 2).toString() + 'px';
          }
        });
      };

      // ToggleAll overwritten to avoid calling it twice when clicking directly on the checkbox, and to avoid changing and emitting the
      // model if we are in responsive mode (with buttons).
      const originalToggleAll = this.pMultiSelect.toggleAll.bind(this.pMultiSelect);
      this.pMultiSelect.toggleAll = (event: Event) => {
        event.stopPropagation();
        this.resetHighlightOptions();

        if (!this.showResponsiveTemplate) {
          originalToggleAll(event);
        } else {
          if (this.disabled || this.pMultiSelect.toggleAllDisabled || this.pMultiSelect.readonly) {
            return;
          }

          let allChecked = this.pMultiSelect.allChecked;
          if (allChecked) {
            this.pMultiSelect.uncheckAll();
          } else {
            this.pMultiSelect.checkAll();
          }

          this.pMultiSelect.onChange.emit({ originalEvent: event, value: this.pMultiSelect.value });
          this.pMultiSelect.updateFilledState();
          event.preventDefault();
        }
      };

      // ToggleAll overwritten to avoid clicking overlay element when we click on the mask (mobile).
      this.pMultiSelect.isOverlayClick = (event: MouseEvent) => {
        const targetNode = event.target as Node;
        const targetHTMLElement = event.target as HTMLElement;
        return !targetHTMLElement.classList.contains('si-selector-multiple_overlay-closable')
          && this.pMultiSelect.overlay
          // isSameNode function is deprectated. Use === instead
          && ((this.pMultiSelect.overlay === targetNode) || this.pMultiSelect.overlay.contains(targetNode));
      };
    }
  }

  highlightPrevOption(): void {
    if (
      !this.pMultiSelect.optionsToRender ||
      !this.pMultiSelect.optionsToRender.length ||
      this.preselectedOptionIndex === 0
    ) {
      return;
    }

    this.highlightOption(this.preselectedOptionIndex - 1, this.preselectedOptionIndex);
    this.preselectedOptionIndex--;
  }

  highlightNextOption(): void {
    if (
      !this.pMultiSelect.optionsToRender ||
      !this.pMultiSelect.optionsToRender.length ||
      this.preselectedOptionIndex === this.pMultiSelect.optionsToRender.length - 1
    ) {
      return;
    }

    this.highlightOption(this.preselectedOptionIndex + 1, this.preselectedOptionIndex);
    this.preselectedOptionIndex++;
  }

  highlightOption(index: number, previousIndex = -1): void {
    const optionElements = this.pMultiSelect.el.nativeElement.querySelector('.p-multiselect-items').children;
    if (previousIndex !== -1) {
      this.renderer.removeClass(optionElements[previousIndex].firstChild, this.highlightClass);
    }

    const elementToHighlight = optionElements[index].firstChild;
    this.renderer.addClass(elementToHighlight, this.highlightClass);

    this.scrollToOption(elementToHighlight);
  }

  resetHighlightOptions(): void {
    // Remove highlight class of previous preselected option.
    if (this.preselectedOptionIndex >= 0) {
      const optionElements = this.pMultiSelect.el.nativeElement.querySelector('.p-multiselect-items').children;
      if (optionElements[this.preselectedOptionIndex]) {
        this.renderer.removeClass(optionElements[this.preselectedOptionIndex].firstChild, this.highlightClass);
      }
    }

    this.preselectedOptionIndex = -1;
  }

  scrollToOption(element): void {
    const wrapperElement = this.pMultiSelect.el.nativeElement.querySelector('.p-multiselect-items-wrapper');

    // Element outside wrapper above (scrolling up).
    const positionTopElement = element['offsetTop'] - wrapperElement.scrollTop;
    if (positionTopElement < 0) {
      wrapperElement.scroll({top: element['offsetTop']});
    }

    // Element outside wrapper below (scrolling down).
    const positionBottomElement = element['offsetTop'] - wrapperElement.scrollTop + element['offsetHeight'];
    const wrapperHeight = wrapperElement['offsetHeight'];
    if (positionBottomElement > wrapperHeight) {
      wrapperElement.scroll({top: element['offsetTop'] + element['offsetHeight'] - wrapperHeight});
      return;
    }
  }

  // Events on the input filter.
  onKeyDownInput(event): void {
    switch (event.key) {
      case keyboardKeys.ARROW_DOWN:
        this.highlightNextOption();
        event.preventDefault();
        break;
      case keyboardKeys.ARROW_UP:
        this.highlightPrevOption();
        event.preventDefault();
        break;
      case keyboardKeys.ENTER:
        const e = {
          originalEvent: event,
          option: this.pMultiSelect.optionsToRender[this.preselectedOptionIndex]
        };
        this.pMultiSelect.onOptionClick(e);
        break;
      case keyboardKeys.TAB:
        if (event.shiftKey) {
          this.shiftTabFromInput = true;
        }

        this.pMultiSelect.hide();
        break;
      default:
    }
  }

  @HostListener('keyup', ['$event'])
  async keyEvent(event: KeyboardEvent) {
    if (event.key === keyboardKeys.TAB) {
      if (event.shiftKey && this.shiftTabFromInput) {
        this.shiftTabFromInput = false;
        return;
      }

      if (!this.pMultiSelect.overlayVisible) {
        await this.onClickMultiSelect();
        this.pMultiSelect.show();
      }
    }
  }

  removePanelListeners(): void {
    this._panelListeners.forEach(fn => fn());
    this._panelListeners = [];
  }

  onClickTooltipIcon(event: any): void {
    this.tooltipIconClick.emit(event);
  }

  ngOnDestroy(): void {
    this.inputFilterSubject.complete();
    this._listeners.forEach(fn => fn());
    this.removePanelListeners();
  }

}
